import { useLayoutEffect, useRef, type MouseEvent } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { useUser } from '@/hooks/use-user';
import {
  getDealsListHref,
  parseDealsListParams,
  rememberDealsListParams,
  serializeDealsListParams,
} from '@/lib/deals-list-url';
import { dealsListState } from '@/states/deals-list.state';
import { useStore } from 'jotai';

export function useDealsListUrlSync(userId: string | undefined) {
  const [params, setParams] = useSearchParams();
  const store = useStore();
  const lastSearch = useRef(params.toString());

  useLayoutEffect(() => {
    const search = params.toString();
    if (search !== lastSearch.current) {
      lastSearch.current = search;
      store.set(dealsListState, parseDealsListParams(params));
    }
    const persist = () => {
      const next = serializeDealsListParams(
        store.get(dealsListState),
        new URLSearchParams(lastSearch.current)
      );
      const search = next.toString();
      rememberDealsListParams(userId, search);
      if (search !== lastSearch.current) {
        lastSearch.current = search;
        setParams(next, { replace: true });
      }
    };
    persist();
    return store.sub(dealsListState, persist);
  }, [params, setParams, store, userId]);
}

export function useDealsListHref() {
  return getDealsListHref(useUser()?.providerUser?.sub);
}

export function useDealsListBackLink() {
  const to = useDealsListHref();
  const { state } = useLocation();
  const navigate = useNavigate();

  return {
    to,
    onClick: (event: MouseEvent<HTMLAnchorElement>) => {
      if (
        state?.fromDealsList !== true ||
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        (event.currentTarget.target && event.currentTarget.target !== '_self')
      )
        return;
      event.preventDefault();
      navigate(-1);
    },
  };
}
